/**
 * Document Type Constants
 * 
 * Comprehensive list of document types organized by categories
 * Used for filtering and categorizing identity documents and other evidence
 */

export const DOCUMENT_TYPES = [
  { value: 'all', text: 'All Types' },
  
  // Identity & Eligibility Documents
  { value: 'PASSPORT', text: 'Passport (valid and in-date)', category: 'Identity & Eligibility Documents', categoryOrder: 1 },
  { value: 'DRIVING_LICENCE', text: 'Driving licence (full/provisional, including categories)', category: 'Identity & Eligibility Documents', categoryOrder: 1 },
  { value: 'BIRTH_CERTIFICATE', text: 'Birth certificate', category: 'Identity & Eligibility Documents', categoryOrder: 1 },
  { value: 'NATIONAL_INSURANCE', text: 'National Insurance card/number', category: 'Identity & Eligibility Documents', categoryOrder: 1 },
  { value: 'RIGHT_TO_WORK', text: 'Proof of right to work (visa, work permit, residency card)', category: 'Identity & Eligibility Documents', categoryOrder: 1 },
  
  // Professional Registration & Memberships
  { value: 'PROFESSIONAL_REGISTRATION', text: 'Professional body registration (HCPC, NMC, GMC, GPhC)', category: 'Professional Registration & Memberships', categoryOrder: 2 },
  { value: 'MEMBERSHIP_CERTIFICATE', text: 'Membership certificates for professional associations', category: 'Professional Registration & Memberships', categoryOrder: 2 },
  { value: 'SPECIALIST_ACCREDITATION', text: 'Specialist accreditation certificates (ALS, NEBOSH, IOSH)', category: 'Professional Registration & Memberships', categoryOrder: 2 },
  { value: 'FITNESS_TO_PRACTISE', text: 'Fitness to practise confirmations', category: 'Professional Registration & Memberships', categoryOrder: 2 },
  
  // Education & Qualifications
  { value: 'ACADEMIC_CERTIFICATE', text: 'Academic certificates (GCSEs, A-levels, Degrees, Diplomas)', category: 'Education & Qualifications', categoryOrder: 3 },
  { value: 'PROFESSIONAL_QUALIFICATION', text: 'Professional qualifications (NVQ, BTEC, Apprenticeships)', category: 'Education & Qualifications', categoryOrder: 3 },
  { value: 'TRANSCRIPT', text: 'Transcripts or detailed results', category: 'Education & Qualifications', categoryOrder: 3 },
  { value: 'APPRENTICESHIP_CERTIFICATE', text: 'Apprenticeship completion certificates', category: 'Education & Qualifications', categoryOrder: 3 },
  
  // Mandatory & Compliance Training
  { value: 'LIFE_SUPPORT_CERTIFICATE', text: 'Basic/Advanced Life Support (BLS/ALS) certificates', category: 'Mandatory & Compliance Training', categoryOrder: 4 },
  { value: 'MANUAL_HANDLING', text: 'Manual Handling/Moving & Handling', category: 'Mandatory & Compliance Training', categoryOrder: 4 },
  { value: 'FIRE_SAFETY', text: 'Fire Safety training', category: 'Mandatory & Compliance Training', categoryOrder: 4 },
  { value: 'SAFEGUARDING', text: 'Safeguarding (Adults & Children)', category: 'Mandatory & Compliance Training', categoryOrder: 4 },
  { value: 'EQUALITY_DIVERSITY', text: 'Equality, Diversity & Inclusion training', category: 'Mandatory & Compliance Training', categoryOrder: 4 },
  { value: 'INFECTION_CONTROL', text: 'Infection Prevention & Control', category: 'Mandatory & Compliance Training', categoryOrder: 4 },
  { value: 'FIRST_AID', text: 'First Aid at Work', category: 'Mandatory & Compliance Training', categoryOrder: 4 },
  { value: 'HEALTH_SAFETY', text: 'Health & Safety training', category: 'Mandatory & Compliance Training', categoryOrder: 4 },
  { value: 'GDPR_DATA_PROTECTION', text: 'GDPR/Data Protection', category: 'Mandatory & Compliance Training', categoryOrder: 4 },
  
  // Specialist or Role-Specific Training
  { value: 'COSHH_TRAINING', text: 'Hazardous substances training (COSHH)', category: 'Specialist or Role-Specific Training', categoryOrder: 5 },
  { value: 'FOOD_HYGIENE', text: 'Food Hygiene & Safety', category: 'Specialist or Role-Specific Training', categoryOrder: 5 },
  { value: 'CONFLICT_RESOLUTION', text: 'Conflict Resolution/De-escalation training', category: 'Specialist or Role-Specific Training', categoryOrder: 5 },
  { value: 'MENTAL_HEALTH_AWARENESS', text: 'Mental Health Awareness/Mental Health First Aid', category: 'Specialist or Role-Specific Training', categoryOrder: 5 },
  { value: 'EMERGENCY_RESPONSE', text: 'Emergency Response training', category: 'Specialist or Role-Specific Training', categoryOrder: 5 },
  { value: 'EQUIPMENT_OPERATION', text: 'Equipment operation training (forklift, medical device)', category: 'Specialist or Role-Specific Training', categoryOrder: 5 },
  
  // Work-Related Evidence
  { value: 'DBS_PVG', text: 'DBS/PVG certificate (with issue date)', category: 'Work-Related Evidence', categoryOrder: 6 },
  { value: 'IMMUNISATION_RECORD', text: 'Immunisation record (Hepatitis B, MMR, TB, etc.)', category: 'Work-Related Evidence', categoryOrder: 6 },
  { value: 'OCCUPATIONAL_HEALTH', text: 'Occupational health clearance', category: 'Work-Related Evidence', categoryOrder: 6 },
  { value: 'SECURITY_CLEARANCE', text: 'Security clearances (CTC, SC, DV)', category: 'Work-Related Evidence', categoryOrder: 6 },
  { value: 'REFERENCES', text: 'References and testimonial letters', category: 'Work-Related Evidence', categoryOrder: 6 },
  { value: 'APPRAISAL', text: 'Appraisals or performance reviews', category: 'Work-Related Evidence', categoryOrder: 6 },
  
  // CPD & Learning Evidence
  { value: 'CPD_CERTIFICATE', text: 'Certificates from CPD courses/webinars', category: 'CPD & Learning Evidence', categoryOrder: 7 },
  { value: 'CONFERENCE_ATTENDANCE', text: 'Attendance records from conferences/seminars', category: 'CPD & Learning Evidence', categoryOrder: 7 },
  { value: 'REFLECTIVE_PRACTICE', text: 'Reflective practice logs', category: 'CPD & Learning Evidence', categoryOrder: 7 },
  { value: 'LEARNING_OUTCOMES', text: 'Learning outcome summaries', category: 'CPD & Learning Evidence', categoryOrder: 7 },
  { value: 'PUBLICATIONS', text: 'Published articles, presentations, or research', category: 'CPD & Learning Evidence', categoryOrder: 7 },
  { value: 'AWARDS', text: 'Awards or recognitions', category: 'CPD & Learning Evidence', categoryOrder: 7 },
  
  // Digital & Skills Evidence
  { value: 'IT_TRAINING', text: 'IT system training (Microsoft Office, EHR systems)', category: 'Digital & Skills Evidence', categoryOrder: 8 },
  { value: 'LANGUAGE_CERTIFICATE', text: 'Language proficiency certificates', category: 'Digital & Skills Evidence', categoryOrder: 8 },
  { value: 'PROJECT_MANAGEMENT', text: 'Project management credentials (PRINCE2, Agile)', category: 'Digital & Skills Evidence', categoryOrder: 8 },
  { value: 'TECHNICAL_SKILLS', text: 'Technical or trade skill certificates', category: 'Digital & Skills Evidence', categoryOrder: 8 },
  
  // Keep OTHER for backwards compatibility and general use
  { value: 'OTHER', text: 'Other', category: 'General', categoryOrder: 9 },
];

/**
 * Get document types formatted for SearchableDropdown component
 * Converts the document types array to the format expected by SearchableDropdown
 * 
 * @returns {Array} Array of objects with title, value, and category properties
 */
export const getSearchableDocumentTypes = () => {
  return DOCUMENT_TYPES.map(option => ({
    title: option.text,
    value: option.value,
    category: option.category || null,
    categoryOrder: option.categoryOrder || 999
  }));
};

/**
 * Get document type display name by value
 * 
 * @param {string} value - The document type value
 * @returns {string} The display text for the document type
 */
export const getDocumentTypeText = (value) => {
  const docType = DOCUMENT_TYPES.find(type => type.value === value);
  return docType ? docType.text : value;
};

/**
 * Get document type category by value
 * 
 * @param {string} value - The document type value  
 * @returns {string|null} The category for the document type
 */
export const getDocumentTypeCategory = (value) => {
  const docType = DOCUMENT_TYPES.find(type => type.value === value);
  return docType ? docType.category : null;
};

/**
 * Get document types for upload (excludes 'all' option)
 * Converts the document types array to the format expected by SearchableDropdown
 * 
 * @returns {Array} Array of objects with title, value, and category properties (without 'all' option)
 */
export const getUploadDocumentTypes = () => {
  return DOCUMENT_TYPES
    .filter(type => type.value !== 'all') // Exclude the 'all' option for uploads
    .map(option => ({
      title: option.text,
      value: option.value,
      category: option.category || null,
      categoryOrder: option.categoryOrder || 999
    }));
};

/**
 * Get all unique categories
 * 
 * @returns {Array} Array of unique category names
 */
export const getDocumentCategories = () => {
  const categories = DOCUMENT_TYPES
    .map(type => type.category)
    .filter(category => category && category !== null);
  return [...new Set(categories)];
};
